// @ts-nocheck

/**
 * MSI3 Enrollment Service
 * 
 * Handles patient enrollment with facility QR code attribution.
 * Determines enrollment city and locks revenue share percentage.
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';
import { MSI3_CONSTANTS } from './constants';
import { marketPenetrationService } from './marketPenetrationService';

const { REVENUE_STATUS, CITY_DETECTION } = MSI3_CONSTANTS;

export const enrollmentService = {
  /**
   * Handle patient enrollment with optional facility attribution
   * @param {Object} enrollmentData - Enrollment details
   * @returns {Promise<Object>} Enrollment result
   */
  async handlePatientEnrollment(enrollmentData) {
    const {
      patientUserId,
      subscriptionTier = 'free',
      facilityCode = null, // From QR code URL parameter
      selectedCityId = null,
      ipAddress = null,
    } = enrollmentData;

    // Check if patient already has an enrollment record
    const existingEnrollment = await this.getEnrollmentByPatient(patientUserId);
    if (existingEnrollment) {
      return {
        success: false,
        error: 'Patient already has an enrollment record',
        existingEnrollment,
      };
    }

    // CASE 1: No facility code - NO revenue share
    if (!facilityCode) {
      const enrollment = await this.createEnrollment({
        patientUserId,
        subscriptionTier,
        enrollingFacilityId: null,
        enrollmentCityId: selectedCityId,
        facilityCityId: null,
        cityDetectionMethod: selectedCityId ? CITY_DETECTION.USER_SELECTED : null,
        countsTowardMarketPenetration: true, // Still counts!
        marketPenetrationAtEnrollment: null,
        lockedRevenueSharePercentage: 0,
        lockedMonthlyAmount: 0,
        currentRevenueFacilityId: null,
        revenueShareStatus: REVENUE_STATUS.NONE,
        inactivityProtectionMonths: MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
      });

      return {
        success: true,
        revenueShare: false,
        reason: 'no_facility_code',
        enrollment,
      };
    }

    // CASE 2: Has facility code - INITIATE revenue share (if paid tier)
    const facilityPartner = await this.getFacilityByCode(facilityCode);
    if (!facilityPartner) {
      return {
        success: false,
        error: 'Invalid or inactive facility code',
      };
    }

    // Determine enrollment city
    const enrollmentCityId = await this.determineEnrollmentCity({
      selectedCityId,
      ipAddress,
      facilityCode,
      facilityCityId: facilityPartner.cityId,
    });

    // Check if in-market enrollment
    const inMarket = enrollmentCityId === facilityPartner.cityId;
    const cityDetectionMethod = selectedCityId
      ? CITY_DETECTION.USER_SELECTED
      : ipAddress
      ? CITY_DETECTION.IP_GEOLOCATION
      : CITY_DETECTION.FACILITY_DEFAULT;

    const outOfMarketReason = inMarket
      ? null
      : `Enrolled in city ${enrollmentCityId} using facility from city ${facilityPartner.cityId}`;

    // Log suspicious activity if out of market
    if (!inMarket) {
      await this.logOutOfMarketEnrollment({
        patientUserId,
        facilityId: facilityPartner.id,
        enrollmentCityId,
        facilityCityId: facilityPartner.cityId,
        ipAddress,
      });
    }

    // Calculate current market penetration FOR THE FACILITY'S CITY
    const penetration = await marketPenetrationService.calculatePenetration(facilityPartner.cityId);

    // Determine revenue share percentage based on penetration
    const revenueSharePct = this.getRevenueSharePercentage(penetration);

    // Calculate monthly amount based on subscription tier and revenue share %
    const monthlyAmount = this.calculateMonthlyRevenue(subscriptionTier, revenueSharePct);

    // Determine revenue share status
    const revenueStatus = subscriptionTier === 'free' ? REVENUE_STATUS.NONE : REVENUE_STATUS.ACTIVE;

    // Create enrollment record with locked-in terms
    const enrollment = await this.createEnrollment({
      patientUserId,
      subscriptionTier,
      enrollingFacilityId: facilityPartner.id,
      enrollmentCityId,
      facilityCityId: facilityPartner.cityId,
      cityDetectionMethod,
      countsTowardMarketPenetration: inMarket,
      outOfMarketReason,
      marketPenetrationAtEnrollment: penetration,
      lockedRevenueSharePercentage: revenueSharePct,
      lockedMonthlyAmount: monthlyAmount,
      currentRevenueFacilityId: subscriptionTier === 'free' ? null : facilityPartner.id,
      lastClinicalContactDate: firebase.firestore.FieldValue.serverTimestamp(),
      revenueShareStatus: revenueStatus,
      inactivityProtectionMonths: facilityPartner.earlyAdopterPosition
        ? MSI3_CONSTANTS.EARLY_ADOPTER_PROTECTION_MONTHS
        : MSI3_CONSTANTS.STANDARD_PROTECTION_MONTHS,
    });

    return {
      success: true,
      revenueShare: subscriptionTier !== 'free',
      facilityId: facilityPartner.id,
      facilityName: facilityPartner.facilityName,
      subscriptionTier,
      revenueSharePct,
      monthlyAmount,
      inMarket,
      warning: inMarket ? null : 'Out-of-market enrollment flagged',
      enrollment,
    };
  },

  /**
   * Create enrollment document in Firestore
   */
  async createEnrollment(data) {
    const enrollmentRef = db.collection('msi3PatientEnrollments').doc();
    const enrollment = {
      id: enrollmentRef.id,
      ...data,
      enrollmentDate: firebase.firestore.FieldValue.serverTimestamp(),
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    };

    await enrollmentRef.set(enrollment);
    return enrollment;
  },

  /**
   * Get enrollment by patient user ID
   */
  async getEnrollmentByPatient(patientUserId) {
    const snapshot = await db
      .collection('msi3PatientEnrollments')
      .where('patientUserId', '==', patientUserId)
      .limit(1)
      .get();

    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  /**
   * Get facility partner by unique signup code
   */
  async getFacilityByCode(uniqueSignupCode) {
    const snapshot = await db
      .collection('msi3FacilityPartners')
      .where('uniqueSignupCode', '==', uniqueSignupCode)
      .where('partnershipStatus', '==', MSI3_CONSTANTS.PARTNERSHIP_STATUS.ACTIVE)
      .limit(1)
      .get();

    if (snapshot.empty) return null;
    return { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
  },

  /**
   * Determine enrollment city using priority order
   */
  async determineEnrollmentCity({ selectedCityId, ipAddress, facilityCode, facilityCityId }) {
    // Priority 1: User-selected city
    if (selectedCityId) {
      return selectedCityId;
    }

    // Priority 2: IP geolocation (TODO: integrate with IP geolocation service)
    if (ipAddress) {
      const geolocatedCityId = await this.geolocateCity(ipAddress);
      if (geolocatedCityId) return geolocatedCityId;
    }

    // Priority 3: Fallback to facility's city
    return facilityCityId;
  },

  /**
   * Geolocate city from IP address
   * TODO: Integrate with MaxMind GeoIP2 or ipapi.co
   */
  async geolocateCity(ipAddress) {
    // Placeholder - return null to fall back to facility city
    return null;
  },

  /**
   * Log out-of-market enrollment for monitoring
   */
  async logOutOfMarketEnrollment(data) {
    await db.collection('msi3OutOfMarketLogs').add({
      ...data,
      timestamp: firebase.firestore.FieldValue.serverTimestamp(),
    });
  },

  /**
   * Get revenue share percentage based on market penetration
   */
  getRevenueSharePercentage(penetration) {
    const { REVENUE_TIERS } = MSI3_CONSTANTS;
    if (penetration <= REVENUE_TIERS.LOW_PENETRATION.maxPercent) {
      return REVENUE_TIERS.LOW_PENETRATION.revenueShare;
    }
    if (penetration <= REVENUE_TIERS.MEDIUM_PENETRATION.maxPercent) {
      return REVENUE_TIERS.MEDIUM_PENETRATION.revenueShare;
    }
    return REVENUE_TIERS.HIGH_PENETRATION.revenueShare;
  },

  /**
   * Calculate monthly revenue for a subscription tier and revenue share percentage
   */
  calculateMonthlyRevenue(subscriptionTier, revenueSharePct) {
    // Default to Tier 1 (Kenya) pricing
    const annualPricing = MSI3_CONSTANTS.PRICING.TIER_1;
    const annualAmount = annualPricing[subscriptionTier] || 0;
    const monthlySubscription = annualAmount / 12;
    const monthlyRevenue = monthlySubscription * (revenueSharePct / 100);
    return monthlyRevenue;
  },

  /**
   * Handle subscription tier change for existing patient
   */
  async handleSubscriptionTierChange(patientUserId, newTier) {
    const enrollment = await this.getEnrollmentByPatient(patientUserId);
    if (!enrollment) {
      return { success: false, error: 'Patient enrollment not found' };
    }

    const oldTier = enrollment.subscriptionTier;

    // Case 1: Upgrade from Free to Paid
    if (oldTier === 'free' && newTier !== 'free') {
      if (enrollment.enrollingFacilityId) {
        const facilityPartner = await db
          .collection('msi3FacilityPartners')
          .doc(enrollment.enrollingFacilityId)
          .get();
        
        if (facilityPartner.exists) {
          const facilityData = facilityPartner.data();
          const currentPenetration = await marketPenetrationService.calculatePenetration(facilityData.cityId);
          const revenueSharePct = this.getRevenueSharePercentage(currentPenetration);
          const monthlyAmount = this.calculateMonthlyRevenue(newTier, revenueSharePct);

          await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
            subscriptionTier: newTier,
            lockedRevenueSharePercentage: revenueSharePct,
            lockedMonthlyAmount: monthlyAmount,
            currentRevenueFacilityId: enrollment.enrollingFacilityId,
            revenueShareStatus: REVENUE_STATUS.ACTIVE,
            lastClinicalContactDate: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
        }
      } else {
        await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
          subscriptionTier: newTier,
          revenueShareStatus: REVENUE_STATUS.NONE,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
    }
    // Case 2: Downgrade from Paid to Free
    else if (oldTier !== 'free' && newTier === 'free') {
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        subscriptionTier: newTier,
        revenueShareStatus: REVENUE_STATUS.NONE,
        currentRevenueFacilityId: null,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }
    // Case 3: Change between paid tiers
    else if (oldTier !== 'free' && newTier !== 'free') {
      const monthlyAmount = this.calculateMonthlyRevenue(newTier, enrollment.lockedRevenueSharePercentage);
      await db.collection('msi3PatientEnrollments').doc(enrollment.id).update({
        subscriptionTier: newTier,
        lockedMonthlyAmount: monthlyAmount,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }

    return { success: true, oldTier, newTier };
  },
};

